// Template commands (CFO plan 4.2). Pure; see tasks.js for the ctx shape. Extra ctx: projectTypes (types),
// templates (taskTemplates docs), listTemplates (a template's list is copied from its list template when here).
import { set, merge, update, del } from './writes.js';
import { makeTaskDoc } from '../taskdoc.js';
import { scaffoldSections, closingSteps, makeProjectType } from '../projecttypes.js';
import { makeTaskTemplate, makeTaskFromTemplate, typeTemplateRefs, starterPack } from '../templates.js';
import { withTag } from '../tags.js';
import { JOB_TYPES } from '../profit.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
const lower = (s) => String(s || '').trim().toLowerCase();

/**
 * A new project of a type starts with the type's phases as sections (numbered in the outline), the closing phase's
 * steps as its first tasks, and the type's task templates as tasks in their phases; the project takes the type's job
 * type and tags where it has none. One group. Used by the phone builder, web project create and the MCP.
 * input = { project (doc, or id in ctx.projects), type, templates? (default ctx.templates) }
 */
export function scaffoldFromType(ctx, { project, type, templates = null }) {
  const p = typeof project === 'string' ? (ctx.projects || []).find((x) => x.id === project) : project;
  if (!p?.id) throw new Error('no project to scaffold');
  if (!type) return { writes: [], sections: 0, tasks: 0 };
  const lib = templates || ctx.templates || [];
  const at = now(ctx);
  const writes = [];
  const secs = scaffoldSections(type);
  const secId = new Map();
  let closingId = null;
  secs.forEach((sec, i) => {
    const id = ctx.newId();
    if (type.closingPhase?.name && i === secs.length - 1) closingId = id;
    if (!secId.has(lower(sec.title))) secId.set(lower(sec.title), id);
    writes.push(set(`sections/${id}`, { id, projectId: p.id, title: sec.title, description: '', order: sec.order, status: sec.status, color: null, completedAt: null, createdAt: at + i }));
  });
  let scope = (ctx.tasks || []).filter((t) => t.projectId === p.id);
  const made = [];
  const add = (input) => {
    const t = makeTaskDoc({ scheduledDate: null, ...input, projectId: p.id }, scope, at + secs.length + made.length, ctx.newId);
    scope = [...scope, t];
    made.push(t);
    writes.push(set(`tasks/${t.id}`, t));
  };
  (closingId ? closingSteps(type) : []).forEach((title) => add({ title, sectionId: closingId }));
  for (const ref of typeTemplateRefs(type)) {
    const tpl = lib.find((x) => x.id === ref.templateId);
    if (!tpl) continue;
    add({ ...makeTaskFromTemplate(tpl, { qty: ref.qty ?? null, projectId: p.id, listTemplates: ctx.listTemplates || [] }), sectionId: ref.phase ? secId.get(lower(ref.phase)) || null : null });
  }
  const patch = {};
  if (p.typeId !== type.id && type.id) patch.typeId = type.id;
  if (!p.jobType && JOB_TYPES.includes(type.jobType)) patch.jobType = type.jobType;
  const tags = (type.tags || []).reduce((acc, t) => withTag(acc, t), Array.isArray(p.tags) ? p.tags : []);
  if (tags.length !== (p.tags || []).length) patch.tags = tags;
  if (Object.keys(patch).length) writes.push(merge(`projects/${p.id}`, { ...patch, updatedAt: at }));
  return { writes, sections: secs.length, tasks: made.length, created: made };
}

/** One task from a template. input = { templateId, qty?, unit?, projectId?, sectionId?, parentId?, scheduledDate?, … }. */
export function addFromTemplate(ctx, { templateId, qty = null, unit = null, projectId = null, ...rest }) {
  const tpl = (ctx.templates || []).find((t) => t.id === templateId);
  if (!tpl) throw new Error(`no task template ${templateId}`);
  const input = { ...makeTaskFromTemplate(tpl, { qty, unit, projectId, listTemplates: ctx.listTemplates || [] }), ...Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== undefined)) };
  const task = makeTaskDoc(input, ctx.tasks || [], now(ctx), ctx.newId);
  return { writes: [set(`tasks/${task.id}`, task)], task };
}

/** Create or replace a task template. */
export function saveTaskTemplate(ctx, tpl) {
  const t = makeTaskTemplate(tpl);
  if (!t.title) throw new Error('title required');
  const id = tpl.id || ctx.newId();
  const at = now(ctx);
  const was = (ctx.templates || []).find((x) => x.id === id);
  const doc = { ...t, id, createdAt: was?.createdAt || tpl.createdAt || at, updatedAt: at };
  return { writes: [set(`taskTemplates/${id}`, doc)], template: doc };
}

/** Delete a task template; types that point at it drop the ref (task list and estimate map). */
export function deleteTaskTemplate(ctx, { id }) {
  if (!(ctx.templates || []).some((t) => t.id === id)) throw new Error(`no task template ${id}`);
  const at = now(ctx);
  const writes = [del(`taskTemplates/${id}`)];
  for (const ty of ctx.projectTypes || []) {
    const refs = Array.isArray(ty.taskTemplates) ? ty.taskTemplates : [];
    const map = Array.isArray(ty.estimateMap) ? ty.estimateMap : [];
    const keepRefs = refs.filter((r) => (typeof r === 'string' ? r : r?.templateId) !== id);
    const keepMap = map.filter((m) => m?.templateId !== id);
    if (keepRefs.length !== refs.length || keepMap.length !== map.length) writes.push(update(`projectTypes/${ty.id}`, { taskTemplates: keepRefs, estimateMap: keepMap, updatedAt: at }));
  }
  return { writes };
}

/** "Save as template": the previewed type + its new task templates (projectToTemplate), in one group. */
export function saveProjectTemplate(ctx, { type, templates = [] }) {
  if (!String(type?.name || '').trim()) throw new Error('name required');
  const at = now(ctx);
  const writes = [];
  const have = new Set((ctx.templates || []).map((t) => t.id));
  for (const t of templates) {
    if (!t.isNew || have.has(t.id)) continue;
    const { isNew, ...rest } = t;
    writes.push(set(`taskTemplates/${t.id}`, { ...makeTaskTemplate(rest), id: t.id, createdAt: at, updatedAt: at }));
  }
  const id = type.id || ctx.newId();
  const doc = { ...makeProjectType(type), id, name: String(type.name).trim(), createdAt: type.createdAt || at, updatedAt: at };
  writes.push(set(`projectTypes/${id}`, doc));
  return { writes, type: doc };
}

/**
 * Add a starter pack (renovation, painting, landscaping) — only on a tap. Existing names are left alone. With its
 * standard departments and their quick tasks (CFO 5.6): missing ones made, existing ones given the quick tasks they lack.
 */
export function applyStarterPack(ctx, { packId, departmentId = null }) {
  const at = now(ctx);
  const r = starterPack(packId, { types: ctx.projectTypes || [], templates: ctx.templates || [], metrics: ctx.metrics || [], departments: ctx.departments || [], newId: ctx.newId, now: at, departmentId });
  const writes = [
    ...r.departments.add.map((d) => set(`departments/${d.id}`, d)),
    ...r.departments.update.map((d) => merge(`departments/${d.id}`, { quickTasks: d.quickTasks, updatedAt: at })),
    ...r.metricDefs.map((m) => set(`metrics/${m.id}`, m)), ...r.templates.map((t) => set(`taskTemplates/${t.id}`, t)), ...r.types.map((t) => set(`projectTypes/${t.id}`, t)),
  ];
  return { writes, ...r };
}
